/**
 * ManifestContext
 * ---------------
 * Demo-grade state for the post-award workflow preview.
 *
 * Pattern mirrors BookmarkContext: useState + localStorage, no backend.
 * The seed orders (fixtures.SEED_ORDERS) hydrate state on first load;
 * subsequent edits persist to localStorage so the demo is repeatable
 * across refreshes.
 *
 * Surface area kept small on purpose — this is a stakeholder mockup,
 * not a real ops product. When v2 lands we'll replace the localStorage
 * adapter with a real /api/orders backend.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { SEED_ORDERS, STEPS } from './fixtures';

const STORAGE_KEY = 'aiquire_manifest_v1';

const ManifestContext = createContext();

export const useManifest = () => {
  const ctx = useContext(ManifestContext);
  if (!ctx) throw new Error('useManifest must be used within ManifestProvider');
  return ctx;
};

const cloneSeeds = () => JSON.parse(JSON.stringify(SEED_ORDERS));

const loadFromStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    return parsed;
  } catch (e) {
    console.warn('[Manifest] failed to read localStorage:', e);
    return null;
  }
};

const saveToStorage = (orders) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(orders));
  } catch (e) {
    console.warn('[Manifest] failed to write localStorage:', e);
  }
};

export const ManifestProvider = ({ children }) => {
  const [orders, setOrders] = useState(() => loadFromStorage() || cloneSeeds());
  const [activeOrderId, setActiveOrderId] = useState(() => orders[0]?.id || null);

  useEffect(() => { saveToStorage(orders); }, [orders]);

  const activeOrder = useMemo(
    () => orders.find((o) => o.id === activeOrderId) || orders[0],
    [orders, activeOrderId],
  );

  // Mutate a single step's state on the active order.
  const setStepState = useCallback((stepId, patch) => {
    setOrders((prev) =>
      prev.map((o) => {
        if (o.id !== activeOrderId) return o;
        const prevStep = o.stepStates?.[stepId] || { stepId, status: 'pending', notes: '', attachments: [] };
        const next = { ...prevStep, ...patch, stepId };
        return { ...o, stepStates: { ...o.stepStates, [stepId]: next } };
      }),
    );
  }, [activeOrderId]);

  // Toggle done <-> in_progress for a step.
  const toggleStepDone = useCallback((stepId) => {
    setOrders((prev) =>
      prev.map((o) => {
        if (o.id !== activeOrderId) return o;
        const prevStep = o.stepStates?.[stepId] || { stepId, status: 'pending', notes: '', attachments: [] };
        const isDone = prevStep.status === 'done';
        const nextStatus = isDone ? 'in_progress' : 'done';
        const next = {
          ...prevStep,
          status: nextStatus,
          completedAt: nextStatus === 'done' ? new Date().toISOString() : null,
          stepId,
        };
        // When marking done, auto-advance: kick the next pending step to in_progress
        const updatedStates = { ...o.stepStates, [stepId]: next };
        if (nextStatus === 'done') {
          const nextStepDef = STEPS.find((s) => s.id === stepId + 1);
          if (nextStepDef) {
            const nx = updatedStates[nextStepDef.id] || { stepId: nextStepDef.id, status: 'pending', notes: '', attachments: [] };
            if (nx.status === 'pending') {
              updatedStates[nextStepDef.id] = { ...nx, status: 'in_progress', stepId: nextStepDef.id };
            }
          }
        }
        return { ...o, stepStates: updatedStates };
      }),
    );
  }, [activeOrderId]);

  // Mutate arbitrary order-level fields (e.g. pickedQuoteFactoryId).
  const updateOrderFields = useCallback((patch) => {
    setOrders((prev) =>
      prev.map((o) => (o.id === activeOrderId ? { ...o, ...patch } : o)),
    );
  }, [activeOrderId]);

  const resetDemo = useCallback(() => {
    const fresh = cloneSeeds();
    setOrders(fresh);
    setActiveOrderId(fresh[0]?.id || null);
  }, []);

  const value = {
    orders,
    activeOrder,
    activeOrderId,
    setActiveOrderId,
    setStepState,
    toggleStepDone,
    updateOrderFields,
    resetDemo,
  };

  return (
    <ManifestContext.Provider value={value}>
      {children}
    </ManifestContext.Provider>
  );
};
